/**
 * Copilote AI Business — first-party analytics (iter-82 rewrite).
 *
 * Dual-writes every canonical funnel event to:
 *   1. MongoDB via /api/analytics/track           ← source of truth
 *   2. GA4 via window.gtag (only if configured)   ← optional
 *
 * The old invoicing-SaaS event schema is preserved for backwards compat
 * (trackEvent + PRICING_PAGE_VIEW etc.), but new code should use the
 * `track(EVENTS.<CANONICAL>, {...})` API below.
 */

const API = process.env.REACT_APP_BACKEND_URL || '';

// ── Canonical event catalog — MUST stay in sync with backend/growth_analytics
export const EVENTS = {
  // Public funnel
  LANDING_VIEWED: 'landing_viewed',
  PRICING_VIEWED: 'pricing_viewed',
  DEMO_VIEWED: 'demo_viewed',
  // iter-120 — démo interactive autonome (funnel : vue → lancement → résultat → CTA → diagnostic → inscription)
  DEMO_STARTED: 'demo_started',
  DEMO_STEP_COMPLETED: 'demo_step_completed',
  DEMO_RESULT_VIEWED: 'demo_result_viewed',
  DEMO_ACTION_APPROVED: 'demo_action_approved',
  DEMO_CTA_CLICKED: 'demo_cta_clicked',
  DEMO_ACTIVATION_STARTED: 'demo_activation_started',
  DEMO_SIGNUP_COMPLETED: 'demo_signup_completed',
  SCORECARD_VIEWED: 'scorecard_viewed',
  // iter-122 — Scorecard (growth_events ; les événements GTM homonymes côté serveur restent inchangés)
  SCORECARD_STARTED: 'scorecard_started',
  SCORECARD_COMPLETED: 'scorecard_completed',
  // iter-122c — boucle virale Scorecard (client ; le serveur garantit 1× par session)
  SCORE_SHARE_OPENED: 'score_share_opened',
  SCORE_SHARE_SHARED: 'score_share_shared',
  SCORE_SHARE_SCORECARD_STARTED: 'score_share_scorecard_started',
  SCORE_SHARE_SCORECARD_COMPLETED: 'score_share_scorecard_completed',
  CTA_CLICKED: 'cta_clicked',

  // Signup
  REGISTER_STARTED: 'register_started',
  REGISTER_COMPLETED: 'register_completed',
  TRIAL_ACTIVATED: 'trial_activated',

  // Product
  DASHBOARD_FIRST_VIEW: 'dashboard_first_view',
  ONBOARDING_STEP: 'onboarding_step_completed',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  CSV_UPLOADED: 'csv_uploaded',
  CAMPAIGN_CREATED: 'campaign_created',
  FIRST_EMAIL_SENT: 'first_email_sent',
  DEMO_BOOKED: 'demo_booked',

  // Revenue
  CHECKOUT_STARTED: 'checkout_started',
  SUBSCRIPTION_ACTIVE: 'subscription_active',
  SUBSCRIPTION_RENEWED: 'subscription_renewed',
  PAYMENT_FAILED: 'payment_failed',
  SUBSCRIPTION_CANCELLED: 'subscription_cancelled',
  CHURN: 'churn',

  // — legacy (invoicing SaaS, still referenced by older components) —
  PRICING_PAGE_VIEW: 'pricing_page_view',
  PRICING_CTA_CLICK: 'pricing_cta_click',
  SALES_CTA_CLICK: 'sales_cta_click',
  SIGNUP_START: 'signup_start',
  SIGNUP_COMPLETE: 'signup_complete',
  TRIAL_START: 'trial_start',
  SUBSCRIPTION_CHECKOUT: 'subscription_checkout',
  SUBSCRIPTION_COMPLETE: 'subscription_complete',
  ONBOARDING_STEP_COMPLETED: 'onboarding_step_completed',
  ONBOARDING_SKIPPED: 'onboarding_skipped',
  FEATURE_USE: 'feature_use',
  NAV_CTA_CLICK: 'nav_cta_click',
};

// ── Session identity (uuid persisted in localStorage + cookie) ──
function uuid4() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export function getSessionId() {
  try {
    let sid = localStorage.getItem('cop_sid');
    if (!sid) {
      // iter-122c — la landing publique d'un score partagé (HTML serveur) pose le cookie AVANT l'app :
      // on le reprend pour que clic → Scorecard → inscription restent la même session (UUID non personnel).
      const m = document.cookie.match(/(?:^|;\s*)cop_sid=([\w-]{8,64})/);
      sid = (m && m[1]) || uuid4();
      localStorage.setItem('cop_sid', sid);
      document.cookie = `cop_sid=${sid}; Path=/; Max-Age=${60 * 60 * 24 * 365}; SameSite=Lax`;
    }
    return sid;
  } catch {
    return uuid4();
  }
}

// ── Acquisition channel (iter-103): capture utm_source/referrer once ──
function getChannel() {
  try {
    let ch = sessionStorage.getItem('cop_utm');
    if (!ch) {
      const p = new URLSearchParams(window.location.search);
      ch = p.get('utm_source')
        || (document.referrer ? new URL(document.referrer).hostname : '')
        || 'direct';
      if (ch.includes(window.location.hostname)) ch = 'direct';
      sessionStorage.setItem('cop_utm', ch);
    }
    return ch;
  } catch {
    return 'direct';
  }
}

// Capture acquisition channel as early as possible (before any route change)
if (typeof window !== 'undefined') {
  try { getChannel(); } catch { /* ignore */ }
}

// ── Canonical event dispatch (new API — use this in new code) ──
export async function track(eventType, properties = {}) {
  if (!eventType) return;
  const sid = getSessionId();
  const enriched = {
    ...properties,
    path: typeof window !== 'undefined' ? window.location.pathname : '',
    locale: typeof navigator !== 'undefined' ? (navigator.language || 'en').slice(0, 5) : 'en',
    utm_source: typeof window !== 'undefined' ? getChannel() : 'direct',
  };

  // 1) First-party — fire-and-forget with keepalive
  try {
    if (API) {
      fetch(`${API}/api/analytics/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_type: eventType,
          session_id: sid,
          properties: enriched,
        }),
        keepalive: true,
      }).catch(() => {});
    }
  } catch { /* analytics must never break real flows */ }

  // 2) GA4 dual-write (only if window.gtag is configured)
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', eventType, { ...enriched, session_id: sid });
    }
  } catch { /* ignore */ }
}

/** Link the current session_id to a real user (call post-login/register). */
export async function identify(userId, email) {
  const sid = getSessionId();
  try {
    if (API) {
      fetch(`${API}/api/analytics/identify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sid, user_id: userId || '', email: email || '' }),
        keepalive: true,
      }).catch(() => {});
    }
  } catch { /* ignore */ }
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function' && userId) {
      window.gtag('set', { user_id: userId });
    }
  } catch { /* ignore */ }
}

// ── Legacy API kept for backwards compatibility with existing components ──
export const trackEvent = (eventName, properties = {}) => {
  track(eventName, properties);
};

export const trackPageView = (pageName, properties = {}) => {
  track('page_view', { page_name: pageName, ...properties });
};

export const trackScrollDepth = (depth) => {
  track('sales_scroll_depth', { depth_percentage: depth });
};

export const initScrollTracking = () => {
  const thresholds = [25, 50, 75, 100];
  const tracked = new Set();
  const checkScroll = () => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (docHeight <= 0) return;
    const scrollPercent = Math.round((scrollTop / docHeight) * 100);
    thresholds.forEach((t) => {
      if (scrollPercent >= t && !tracked.has(t)) {
        tracked.add(t);
        trackScrollDepth(t);
      }
    });
  };
  window.addEventListener('scroll', checkScroll, { passive: true });
  return () => window.removeEventListener('scroll', checkScroll);
};

export const usePageTracking = (pageName) => {
  if (typeof window !== 'undefined') {
    trackPageView(pageName);
  }
};

export const getStoredEvents = () => [];
export const clearStoredEvents = () => {};

const Analytics = {
  EVENTS, track, identify, trackEvent, trackPageView, trackScrollDepth,
  initScrollTracking, usePageTracking, getStoredEvents, clearStoredEvents,
};

export default Analytics;
